// ─────────────────────────────────────────────────────────────────────────────
// src/components/TabBar.tsx
// Barre d'onglets style Google Chrome — intégrée sous l'AppBar NAWARA BI.
// • Onglets persistants (localStorage via useTabs)
// • Glisser-déposer pour réordonner
// • Menu contextuel clic-droit
// • Bouton "+" pour ouvrir une page
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import { PAGE_META, Tab } from "./useTabs";
import { Page } from "./AppBar";
import { colors } from "../shared/ui/tokens";
import { useBreakpoint } from "../shared/ui/useBreakpoint";

// ── Palette de la barre d'onglets (thème clair) ───────────────────────────────
const TAB = {
  barBg: colors.neutral[0],
  border: colors.neutral[200],
  tabBg: colors.neutral[0],
  tabHover: colors.neutral[100],
  tabActiveBg: colors.brand[50],
  tabActiveBorder: colors.brand[200],
  indicator: colors.brand[600],
  text: colors.neutral[600],
  textActive: colors.brand[700],
  closeText: colors.neutral[400],
  closeHoverBg: colors.danger[50],
  closeHoverText: colors.danger[600],
  accent: colors.brand[600],
  accentSoft: colors.brand[50],
  accentRing: colors.brand[200],
  newTabBg: colors.neutral[100],
  menuBg: colors.neutral[0],
  menuShadow: "0 12px 32px rgba(15,23,42,0.12), 0 2px 6px rgba(15,23,42,0.06)",
  menuItem: colors.neutral[700],
  menuHover: colors.neutral[100],
  menuHeading: colors.neutral[500],
  danger: colors.danger[600],
  dangerHover: colors.danger[50],
};

// ── Pages disponibles pour le "new tab" picker ────────────────────────────────
const NEW_TAB_PAGES: Page[] = [
  "accueil",
  "dashboard",
  "mdx",
  "magazin",
  "marques",
  "ventes",
  "sousfamille",
  "saison",
  "groupe",
  "univers",
  "taille",
  "couleur",
  "couleur-taille",
  "famille",
  "vendeur",
  "profile",
];

// ── Props ─────────────────────────────────────────────────────────────────────
interface TabBarProps {
  tabs: Tab[];
  activeTabId: string | null;
  onActivate: (id: string) => void;
  onClose: (id: string) => void;
  onNewTab: (page: Page) => void;
  onMoveTab: (fromIndex: number, toIndex: number) => void;
  onCloseOthers: (id: string) => void;
  onCloseAll: () => void;
  onDuplicate: (id: string) => void;
}

// ── Context menu ──────────────────────────────────────────────────────────────
interface CtxMenu {
  tabId: string;
  x: number;
  y: number;
}

// ── New-tab picker ────────────────────────────────────────────────────────────
interface Picker {
  anchorRight: number; // px from right edge of bar
  anchorTop: number;
}

// ─────────────────────────────────────────────────────────────────────────────
const TabBar: React.FC<TabBarProps> = ({
  tabs,
  activeTabId,
  onActivate,
  onClose,
  onNewTab,
  onMoveTab,
  onCloseOthers,
  onCloseAll,
  onDuplicate,
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [ctxMenu, setCtxMenu] = useState<CtxMenu | null>(null);
  const { isMobile } = useBreakpoint();
  const [picker, setPicker] = useState<Picker | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  // ── Scroll active tab into view ──────────────────────────────────────────
  useLayoutEffect(() => {
    if (!scrollRef.current || !activeTabId) return;
    const el = scrollRef.current.querySelector<HTMLElement>(
      `[data-tabid="${activeTabId}"]`,
    );
    if (el)
      el.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "smooth",
      });
  }, [activeTabId, tabs.length]);

  // ── Close menus on outside click ─────────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      setCtxMenu(null);
      setPicker(null);
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  // ── Context menu (right-click) ───────────────────────────────────────────
  const handleContextMenu = useCallback((e: React.MouseEvent, tab: Tab) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ tabId: tab.id, x: e.clientX, y: e.clientY });
    setPicker(null);
  }, []);

  // ── Drag & drop ──────────────────────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDragFrom(index);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOver(index);
  };
  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragFrom !== null && dragFrom !== index) {
      onMoveTab(dragFrom, index);
    }
    setDragFrom(null);
    setDragOver(null);
  };
  const handleDragEnd = () => {
    setDragFrom(null);
    setDragOver(null);
  };

  // ── New tab picker toggle ─────────────────────────────────────────────────
  const handleNewTabClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (picker) {
      setPicker(null);
      return;
    }
    const btn = e.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    const barRect = barRef.current?.getBoundingClientRect();
    setPicker({
      anchorRight: barRect ? window.innerWidth - barRect.right + 8 : 8,
      anchorTop: rect.bottom + 4,
    });
    setCtxMenu(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Tab strip ──────────────────────────────────────────────────────── */}
      <div
        ref={barRef}
        style={{
          background: TAB.barBg,
          borderBottom: `1px solid ${TAB.border}`,
          display: "flex",
          alignItems: "flex-end",
          height: 40,
          position: "relative",
          zIndex: 90,
          userSelect: "none",
        }}
      >
        {/* Scrollable tabs area */}
        <div
          ref={scrollRef}
          style={{
            display: "flex",
            alignItems: "flex-end",
            flex: 1,
            overflowX: "auto",
            overflowY: "hidden",
            height: "100%",
            scrollbarWidth: "none",
          }}
        >
          {tabs.map((tab, index) => {
            const isActive = tab.id === activeTabId;
            const isDraggingOver = dragOver === index && dragFrom !== index;

            return (
              <div
                key={tab.id}
                data-tabid={tab.id}
                draggable={!tab.pinned}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                onContextMenu={(e) => handleContextMenu(e, tab)}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = TAB.tabHover;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = TAB.tabBg;
                }}
                onClick={() => {
                  onActivate(tab.id);
                  setCtxMenu(null);
                }}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "0 12px 0 10px",
                  minWidth: isMobile ? 108 : 140,
                  maxWidth: isMobile ? 160 : 220,
                  height: isActive ? 36 : 32,
                  marginBottom: 0,
                  marginTop: "auto",
                  cursor: "pointer",
                  flexShrink: 0,
                  borderRadius: isActive ? "8px 8px 0 0" : "6px 6px 0 0",
                  background: isActive
                    ? TAB.tabActiveBg
                    : isDraggingOver
                      ? TAB.tabHover
                      : TAB.tabBg,
                  border: `1px solid ${isActive ? TAB.tabActiveBorder : TAB.border}`,
                  borderBottom: isActive
                    ? `1px solid ${TAB.tabActiveBg}`
                    : "1px solid transparent",
                  transition: "background 0.15s, height 0.10s",
                  outline: isDraggingOver ? `2px dashed ${TAB.accent}` : "none",
                  zIndex: isActive ? 2 : 1,
                }}
              >
                {/* Tab icon */}
                <span style={{ fontSize: 13, flexShrink: 0, lineHeight: 1 }}>
                  {PAGE_META[tab.page]?.icon ?? tab.icon}
                </span>

                {/* Tab label */}
                <span
                  style={{
                    flex: 1,
                    fontSize: 11,
                    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? TAB.textActive : TAB.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: 1.3,
                    transition: "color 0.15s",
                  }}
                >
                  {tab.label}
                </span>

                {/* Close button (hidden for pinned) */}
                {!tab.pinned && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(tab.id);
                    }}
                    title="Fermer"
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: "none",
                      background: "transparent",
                      color: TAB.closeText,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      flexShrink: 0,
                      lineHeight: 1,
                      padding: 0,
                      transition: "background 0.15s, color 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      const btn = e.currentTarget;
                      btn.style.background = TAB.closeHoverBg;
                      btn.style.color = TAB.closeHoverText;
                    }}
                    onMouseLeave={(e) => {
                      const btn = e.currentTarget;
                      btn.style.background = "transparent";
                      btn.style.color = TAB.closeText;
                    }}
                  >
                    ✕
                  </button>
                )}

                {/* Active indicator (bottom glow) */}
                {isActive && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 12,
                      right: 12,
                      height: 2,
                      borderRadius: "2px 2px 0 0",
                      background: TAB.indicator,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* ── New tab button ──────────────────────────────────────────────── */}
        <button
          onClick={handleNewTabClick}
          title="Nouvel onglet"
          style={{
            width: 30,
            height: 30,
            borderRadius: 6,
            border: "none",
            margin: "auto 8px auto 4px",
            background: picker ? TAB.accentSoft : TAB.newTabBg,
            color: TAB.accent,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            flexShrink: 0,
            transition: "background 0.15s, transform 0.15s",
            boxShadow: picker ? `0 0 0 1.5px ${TAB.accentRing}` : "none",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background = TAB.accentSoft;
            (e.currentTarget as HTMLElement).style.transform = "scale(1.12)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = picker
              ? TAB.accentSoft
              : TAB.newTabBg;
            (e.currentTarget as HTMLElement).style.transform = "scale(1)";
          }}
        >
          +
        </button>
      </div>

      {/* ── Context menu ─────────────────────────────────────────────────────── */}
      {ctxMenu && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: ctxMenu.y,
            right: ctxMenu.x,
            zIndex: 9999,
            background: TAB.menuBg,
            border: `1px solid ${TAB.border}`,
            borderRadius: 12,
            boxShadow: TAB.menuShadow,
            minWidth: 240,
            padding: "12px 0 8px",
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            animation: "nwCtxFade 0.16s ease-out",
            maxHeight: 4 * 44 + 44, // ← 4 items (~44px chacun) + header (44px)
            overflowY: "auto", // ← scroll si plus de 4
            scrollbarWidth: "thin",
            scrollbarColor: `${TAB.border} transparent`,
          }}
        >
          {[
            {
              label: "Dupliquer l'onglet",
              icon: "⊕",
              action: () => {
                onDuplicate(ctxMenu.tabId);
                setCtxMenu(null);
              },
            },
            {
              label: "Fermer les autres",
              icon: "⊗",
              action: () => {
                onCloseOthers(ctxMenu.tabId);
                setCtxMenu(null);
              },
            },
            {
              label: "Fermer tous les onglets",
              icon: "✕",
              danger: true,
              action: () => {
                onCloseAll();
                setCtxMenu(null);
              },
            },
            {
              label: "Fermer cet onglet",
              icon: "✕",
              danger: true,
              action: () => {
                onClose(ctxMenu.tabId);
                setCtxMenu(null);
              },
            },
          ].map((item, i) => (
            <button
              key={i}
              onClick={item.action}
              style={{
                width: "100%",
                padding: "9px 16px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: item.danger ? TAB.danger : TAB.menuItem,
                fontSize: 12,
                fontWeight: 600,
                transition: "background 0.13s",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = item.danger
                  ? TAB.dangerHover
                  : TAB.menuHover;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  "transparent";
              }}
            >
              <span
                style={{
                  fontSize: 14,
                  width: 20,
                  textAlign: "center",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </span>
              {item.label}
            </button>
          ))}
        </div>
      )}

      {/* ── New-tab page picker ───────────────────────────────────────────────── */}
      {picker && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: picker.anchorTop,
            right: picker.anchorRight,
            zIndex: 9999,
            background: TAB.menuBg,
            border: `1px solid ${TAB.border}`,
            borderRadius: 12,
            boxShadow: TAB.menuShadow,
            minWidth: 240,
            padding: "12px 0 8px",
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            animation: "nwCtxFade 0.16s ease-out",
            maxHeight: 220,
            overflowY: "auto",
            scrollbarWidth: "thin" as const,
            scrollbarColor: `${TAB.border} transparent`,
          }}
        >
          <div
            style={{
              padding: "0 16px 10px",
              fontSize: 10,
              fontWeight: 800,
              color: TAB.menuHeading,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              borderBottom: `1px solid ${TAB.border}`,
              marginBottom: 6,
            }}
          >
            Ouvrir dans un nouvel onglet
          </div>

          {NEW_TAB_PAGES.map((page) => {
            const meta = PAGE_META[page];
            return (
              <button
                key={page}
                onClick={() => {
                  onNewTab(page);
                  setPicker(null);
                }}
                style={{
                  width: "100%",
                  padding: "8px 16px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  color: TAB.menuItem,
                  fontSize: 12,
                  fontWeight: 600,
                  transition: "background 0.13s",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    TAB.menuHover;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "transparent";
                }}
              >
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 7,
                    background: TAB.accentSoft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {meta.icon}
                </span>
                <span>{meta.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Global animations ─────────────────────────────────────────────────── */}
      <style>{`
        @keyframes nwCtxFade {
          from { opacity: 0; transform: translateY(-6px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        /* Hide scrollbar on tab strip */
        div[style*="overflowX: auto"]::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

export default TabBar;
